// Sale items only — products explicitly marked with the sale flag
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ProductCard } from '../../components/shopper/ProductCard';
import { ShopperNavActions } from '../../components/shopper/ShopperNavActions';
import { BackButton } from '../../components/ui/BackButton';
import { SHOPPER_HOME } from '../../constants/routes';
import { useSearch, SearchFilters, SearchProduct } from '../../hooks/useSearch';
import { useCartContext } from '../../hooks/useCart';
import { useFavoritesContext } from '../../hooks/useFavorites';
import { FASH_LOGO } from '../../constants/brand';

export function SalePage() {
  const navigate = useNavigate();
  const cart = useCartContext();
  const favorites = useFavoritesContext();

  const filters: SearchFilters = useMemo(() => ({ sale: true }), []);

  const { results, loading } = useSearch({
    debounceMs: 200,
    hitsPerPage: 60,
    filters,
  });

  const hits = results?.hits ?? [];
  const totalHits = results?.totalHits ?? 0;

  const handleProductClick = (id: string) => navigate(`/product/${id}`, { state: { from: '/sale' } });
  const handleAddToCart = (product: SearchProduct, selectedSize?: string) => {
    const size = product.sizes?.find((item) => item.label === selectedSize && item.available);
    if (!size) {
      navigate(`/product/${product.id}`, { state: { from: '/sale' } });
      return;
    }
    cart.addItem({
      productId: product.id,
      name: product.name,
      brand: product.brand,
      businessId: product.brandId || '',
      price: product.price,
      size: size.label,
      quantity: 1,
      imageUrl: product.imageUrl || product.images?.[0] || '',
    });
  };

  return (
    <div className="min-h-screen bg-white pb-24">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <BackButton
              fallback={SHOPPER_HOME}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
              iconClassName="h-5 w-5 text-heading"
            />
            <Link to={SHOPPER_HOME} className="flex-1 flex justify-center">
              <img src={FASH_LOGO} alt="Fash" className="h-10 object-contain" />
            </Link>
            <ShopperNavActions className="p-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0" iconClassName="h-5 w-5 text-heading" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-2">
          <Tag className="h-6 w-6 text-primary" aria-hidden />
          <h1 className="text-2xl md:text-3xl font-serif text-heading">Sale</h1>
        </div>
        <p className="text-sm text-body mb-6">
          Items exclusively marked on sale by our boutiques.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : hits.length === 0 ? (
          <div className="text-center py-16 text-body">
            <p>No sale items at the moment.</p>
            <Link to="/categories" className="mt-4 inline-block text-primary font-medium hover:underline">
              Browse categories
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {hits.map((product) => (
              <div
                key={product.id}
                onClick={() => handleProductClick(product.id)}
                className="cursor-pointer"
              >
                <ProductCard
                  product={{
                    id: product.id,
                    name: product.name,
                    brand: product.brand,
                    price: product.price,
                    compareAtPrice: product.compareAtPrice,
                    imageUrl: product.imageUrl || product.images?.[0] || '',
                    sizes: product.sizes,
                  }}
                  onAddToCart={(size, e) => {
                    e?.stopPropagation?.();
                    handleAddToCart(product, size);
                  }}
                  onFavorite={() =>
                    favorites.toggleFavorite({
                      productId: product.id,
                      name: product.name,
                      brand: product.brand,
                      price: product.price,
                      imageUrl: product.imageUrl || product.images?.[0] || '',
                    })
                  }
                  isFavorited={favorites.isFavorited(product.id)}
                  compact
                />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
